import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { GA4_EVENT_VIEW_PROMOTION } from '@/data/constants/gtm';

import { useInitialLoadingFinished } from 'presentation/events/hooks/useInitialLoadingFinished';
import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import {
	GA4ViewPromotionEventItemPayload,
	GA4ViewPromotionEventPayload,
} from 'presentation/events/types/ga4Types';
import {
	getCreativeNameFromPathname,
	getPromotionNameFromGtmClassElement,
} from 'presentation/events/utils/promotionsUtils';

export const useTrackViewPromotion = () => {
	const pathname = usePathname();
	const initialLoadingFinished = useInitialLoadingFinished();

	useEffect(() => {
		if (!pathname || !initialLoadingFinished) {
			return;
		}

		// Get all [data-div] elements
		const elements = document.querySelectorAll('[data-div]');

		const seenPromotionIds = new Set<string>();

		const onIntersect = (entries: IntersectionObserverEntry[]) => {
			const items: GA4ViewPromotionEventItemPayload[] = [];

			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}

				const promotionId = entry.target.getAttribute('data-div');

				if (!promotionId) {
					return;
				}

				const seenPromotionId = seenPromotionIds.has(promotionId);

				if (seenPromotionId) {
					return;
				}

				seenPromotionIds.add(promotionId);

				// Get all .gtm_class elements of current [data-div] element
				const gtmClassElements = entry.target.querySelectorAll('.gtm_class');

				const seenPromotionNames = new Set<string>();

				const articleToCreativeSlot = new Map<Element, number>();

				let creativeSlot = 0;

				gtmClassElements.forEach((gtmClassElement) => {
					const promotionName = getPromotionNameFromGtmClassElement(gtmClassElement);

					if (!promotionName) {
						return;
					}

					const seenPromotionName = seenPromotionNames.has(promotionName);

					if (seenPromotionName) {
						return;
					}

					seenPromotionNames.add(promotionName);

					const articleAncestor = gtmClassElement.closest('article');
					let resolvedCreativeSlot: number;

					if (articleAncestor && articleToCreativeSlot.has(articleAncestor)) {
						resolvedCreativeSlot = articleToCreativeSlot.get(articleAncestor) as number;
					} else {
						creativeSlot += 1;
						resolvedCreativeSlot = creativeSlot;

						if (articleAncestor) {
							articleToCreativeSlot.set(articleAncestor, resolvedCreativeSlot);
						}
					}

					items.push({
						promotion_id: promotionId,
						promotion_name: promotionName,
						creative_name: getCreativeNameFromPathname(pathname),
						creative_slot: resolvedCreativeSlot.toString(),
					});
				});
			});

			if (!items.length) {
				return;
			}

			// Send GA4 event
			const payload: GA4ViewPromotionEventPayload = {
				event: GA4_EVENT_VIEW_PROMOTION,
				ecommerce: {
					items,
				},
			};

			triggerGA4EcommerceEvent(payload);
		};

		// Create an intersection observer for the [data-div] elements
		const observer = new IntersectionObserver(onIntersect, {
			threshold: 0.15,
		});

		elements.forEach((element) => observer.observe(element));

		return () => {
			elements.forEach((element) => observer.unobserve(element));
		};
	}, [initialLoadingFinished, pathname]);
};
